import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { auth } from '../../firebase/config';
import { getViewerTimezone } from '../../utils/dateTime';
import './TransactionInbox.css';

// InboundEmail.receivedAt / OutboundEmail.sentAt come back from
// notification-service as a bare LocalDateTime (UTC digits, no 'Z') — a naive
// `new Date(dateString)` would misparse it as browser-local, throwing off
// both the "Xh/Xd ago" duration math and the date fallback. Append the
// missing 'Z' before parsing so the underlying instant — and everything
// derived from it — is correct.
const toUtcDate = (dateString) => {
  const hasZone = /[Zz]|[+-]\d{2}:\d{2}$/.test(dateString || '');
  return new Date(hasZone ? dateString : `${dateString}Z`);
};

const formatDate = (dateString) => {
  const date = toUtcDate(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: getViewerTimezone() });
};

const formatFullDate = (dateString) => {
  const date = toUtcDate(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: getViewerTimezone(),
  });
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getStatusColor = (status) => {
  switch (status) {
    case 'MATCHED': return '#4CAF50';
    case 'UNMATCHED': return '#FF9800';
    case 'ERROR': return '#F44336';
    default: return '#9E9E9E';
  }
};

// Delivery-status pill copy + color for outbound rows — copied verbatim from mobile's
// TransactionInboxScreen deliveryStatusMeta (rialty-mobile-app). Terminal failures are
// red, engagement is green, in-flight states are amber/grey.
const deliveryStatusMeta = (status) => {
  switch (status) {
    case 'DELIVERED': return { label: 'Delivered', color: '#4CAF50' };
    case 'OPENED': return { label: 'Opened', color: '#4CAF50' };
    case 'CLICKED': return { label: 'Clicked', color: '#4CAF50' };
    case 'BOUNCED': return { label: 'Bounced', color: '#F44336' };
    case 'DROPPED': return { label: 'Not delivered', color: '#F44336' };
    case 'SPAMREPORT': return { label: 'Marked as spam', color: '#F44336' };
    case 'UNSUBSCRIBED': return { label: 'Unsubscribed', color: '#F44336' };
    case 'FAILED': return { label: 'Send failed', color: '#F44336' };
    case 'DEFERRED': return { label: 'Deferred', color: '#FF9800' };
    case 'PROCESSED': return { label: 'Sent', color: '#7854B3' };
    case 'QUEUED': return { label: 'Queued', color: '#7854B3' };
    default: return { label: 'Sent', color: '#7854B3' };
  }
};

// Sort order for the per-recipient breakdown: failures first (the thing the agent needs
// to fix), then engagement, then in-flight. Copied verbatim from mobile.
const RECIPIENT_STATUS_RANK = {
  BOUNCED: 0, DROPPED: 0, SPAMREPORT: 0, UNSUBSCRIBED: 0, FAILED: 0,
  DEFERRED: 1,
  QUEUED: 2, PROCESSED: 2,
  DELIVERED: 3,
  OPENED: 4, CLICKED: 4,
};
const recipientStatusRank = (s) => (s in RECIPIENT_STATUS_RANK ? RECIPIENT_STATUS_RANK[s] : 5);

const outboundRecipientLabel = (email) => {
  const to = email.toEmails || [];
  if (to.length === 0) return '(no recipient)';
  if (to.length === 1) return to[0];
  return `${to[0]} +${to.length - 1}`;
};

// Total recipients across to + cc + bcc — decides whether the per-recipient breakdown is
// worth showing (single-recipient sends already convey the info via the top-level pill).
const totalRecipientCount = (email) =>
  (email.toEmails?.length || 0) + (email.ccEmails?.length || 0) + (email.bccEmails?.length || 0);

const extractSenderName = (fromEmail) => {
  const match = fromEmail.match(/^(.+?)\s*<.+>$/);
  if (match) return match[1].replace(/^["']|["']$/g, '');
  return fromEmail.split('@')[0];
};

const extractEmailAddress = (fromEmail) => {
  const match = fromEmail.match(/<(.+?)>$/);
  if (match) return match[1];
  return fromEmail;
};

const getInitials = (name) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const getFileIcon = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf': return { icon: '📄', color: '#E53935' };
    case 'doc': case 'docx': return { icon: '📝', color: '#1976D2' };
    case 'xls': case 'xlsx': return { icon: '📊', color: '#388E3C' };
    case 'png': case 'jpg': case 'jpeg': case 'gif': return { icon: '🖼️', color: '#7B1FA2' };
    case 'zip': case 'rar': return { icon: '📦', color: '#795548' };
    default: return { icon: '📎', color: '#666' };
  }
};

const DirectionBadge = ({ direction }) => {
  const isReceived = direction === 'received';
  const color = isReceived ? '#2F1971' : '#7854B3';
  return (
    <span className="txn-inbox-direction-badge" style={{ backgroundColor: color + '15', color }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        {isReceived
          ? <><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></>
          : <><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></>}
      </svg>
      {isReceived ? 'Received' : 'Sent'}
    </span>
  );
};

const RecipientRow = ({ recipient }) => {
  const meta = deliveryStatusMeta(recipient.deliveryStatus);
  return (
    <div className="txn-sent-recipient-row">
      <span className="txn-sent-recipient-email">{recipient.email}</span>
      <span className="txn-sent-recipient-pill" style={{ backgroundColor: meta.color + '20', color: meta.color }}>
        {meta.label}
      </span>
    </div>
  );
};

const SentEmailCard = ({ email }) => {
  const meta = deliveryStatusMeta(email.deliveryStatus);
  const recipientLabel = outboundRecipientLabel(email);
  const dateSource = email.sentAt || email.lastEventAt;
  const totalRecipients = totalRecipientCount(email);
  // Server-computed rollup (OutboundEmailRollupService) — read directly, never
  // recomputed client-side from events[]. Only surfaced for multi-recipient sends;
  // for a single recipient the top-level pill already is the answer.
  const perRecipient = totalRecipients > 1
    ? [...(email.recipientStatuses || [])].sort(
        (a, b) => recipientStatusRank(a.deliveryStatus) - recipientStatusRank(b.deliveryStatus)
      )
    : [];

  return (
    <div className="txn-inbox-card txn-sent-card">
      <div className="txn-inbox-card-row">
        <div className="txn-inbox-avatar txn-sent-avatar">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </div>
        <div className="txn-inbox-card-content">
          <div className="txn-inbox-card-top">
            <span className="txn-inbox-sender">To: {recipientLabel}</span>
            {dateSource && <span className="txn-inbox-date">{formatDate(dateSource)}</span>}
          </div>
          <div className="txn-inbox-subject">{email.subject || '(No subject)'}</div>
          {email.bounceReason && (
            <div className="txn-sent-bounce-reason">{email.bounceReason}</div>
          )}
          <div className="txn-inbox-meta">
            <DirectionBadge direction="sent" />
            <span
              className="txn-inbox-status-badge"
              style={{ backgroundColor: meta.color + '20', color: meta.color }}
            >
              {meta.label}
            </span>
            {email.openCount > 0 && (
              <span className="txn-inbox-attachment-badge">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                </svg>
                {email.openCount}
              </span>
            )}
          </div>
          {perRecipient.length > 0 && (
            <div className="txn-sent-recipient-breakdown">
              {perRecipient.map(r => <RecipientRow key={r.email} recipient={r} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TransactionInbox = ({ transaction, onEmailCountChange }) => {
  const [inbound, setInbound] = useState([]);
  const [outbound, setOutbound] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [activeTab, setActiveTab] = useState('inbox'); // 'inbox' | 'sent'
  const [downloadingKey, setDownloadingKey] = useState(null); // `${emailId}:${idx}` while a download is in flight

  const getFirebaseToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (auth.currentUser) {
        auth.currentUser.getIdToken(true).then(resolve).catch(reject);
        return;
      }
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error('Firebase auth timeout'));
      }, 10000);
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        clearTimeout(timeoutId);
        unsubscribe();
        if (user) {
          try {
            const token = await user.getIdToken(true);
            resolve(token);
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error('No authenticated user'));
        }
      });
    });
  }, []);

  const fetchEmails = useCallback(async () => {
    if (!transaction?.id) return;
    try {
      setIsLoading(true);
      setError(null);
      const firebaseToken = await getFirebaseToken();
      const headers = { Authorization: `Bearer ${firebaseToken}` };
      // allSettled: an outbound-fetch failure degrades gracefully — the Inbox tab still
      // renders, the Sent tab just shows empty (mirrors mobile TransactionInboxScreen).
      const [inboundResult, outboundResult] = await Promise.allSettled([
        apiClient.get(API_ENDPOINTS.inboundEmailByTransaction(transaction.id), { headers }),
        apiClient.get(API_ENDPOINTS.outboundEmailByTransaction(transaction.id), { headers }),
      ]);

      if (inboundResult.status === 'fulfilled') {
        const data = inboundResult.value.data || [];
        setInbound(data);
        if (onEmailCountChange) onEmailCountChange(data.length);
      } else {
        console.error('Error fetching inbox:', inboundResult.reason);
        setError(inboundResult.reason?.message || 'Failed to load inbox');
      }

      if (outboundResult.status === 'fulfilled') {
        setOutbound(outboundResult.value.data || []);
      } else {
        console.warn('Outbound fetch failed (delivery status hidden):', outboundResult.reason);
        setOutbound([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [transaction?.id, getFirebaseToken, onEmailCountChange]);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  // Attachment downloads go through the auth-gated notification-service endpoint,
  // so we can't wire a bare <a href> — fetch as a blob with the Firebase token,
  // then trigger the browser save via a temporary anchor.
  const handleDownloadAttachment = useCallback(async (emailId, attachmentIndex, filename) => {
    const key = `${emailId}:${attachmentIndex}`;
    try {
      setDownloadingKey(key);
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(
        API_ENDPOINTS.inboundEmailAttachmentDownload(emailId, attachmentIndex),
        {
          headers: { Authorization: `Bearer ${firebaseToken}` },
          responseType: 'blob',
        },
      );
      const blobUrl = window.URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Attachment download failed:', err);
      alert('Could not download the attachment. Please try again.');
    } finally {
      setDownloadingKey(null);
    }
  }, [getFirebaseToken]);

  const sortedInbound = useMemo(
    () => [...inbound].sort((a, b) => new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime()),
    [inbound]
  );
  const sortedOutbound = useMemo(
    () => [...outbound].sort(
      (a, b) => new Date(b.sentAt || b.lastEventAt || 0).getTime() - new Date(a.sentAt || a.lastEventAt || 0).getTime()
    ),
    [outbound]
  );

  const transactionSubtitle = transaction?.transactionName || transaction?.propertyAddress || transaction?.dealName || '';

  // Email detail view (inbound only — outbound rows aren't tappable; the per-recipient
  // breakdown is inline on the card, matching mobile's "no detail screen" decision)
  if (selectedEmail) {
    const senderName = extractSenderName(selectedEmail.fromEmail);
    const senderEmail = extractEmailAddress(selectedEmail.fromEmail);

    return (
      <div className="txn-inbox-section">
        {/* Detail Header */}
        <div className="txn-inbox-detail-header">
          <button className="txn-inbox-back-btn" onClick={() => setSelectedEmail(null)}>
            <span>←</span>
            <span>Back to Inbox</span>
          </button>
        </div>

        {/* Subject */}
        <div className="txn-inbox-detail-subject-row">
          <h2 className="txn-inbox-detail-subject">{selectedEmail.subject || '(No subject)'}</h2>
          <span className="txn-inbox-detail-date">{formatFullDate(selectedEmail.receivedAt)}</span>
        </div>

        {/* Sender Card */}
        <div className="txn-inbox-detail-sender">
          <div className="txn-inbox-avatar txn-inbox-avatar-lg">
            {getInitials(senderName)}
          </div>
          <div className="txn-inbox-detail-sender-info">
            <span className="txn-inbox-detail-sender-name">{senderName}</span>
            <span className="txn-inbox-detail-sender-email">{senderEmail}</span>
          </div>
        </div>

        {/* Recipient */}
        <div className="txn-inbox-detail-recipient">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
          </svg>
          <span className="txn-inbox-detail-to-label">To:</span>
          <span className="txn-inbox-detail-to-value">{selectedEmail.toEmail}</span>
        </div>

        <div className="txn-inbox-detail-divider" />

        {/* Body */}
        <div className="txn-inbox-detail-body">
          {selectedEmail.textBody || '(No text content)'}
        </div>

        {/* Attachments */}
        {selectedEmail.attachmentMetadata && selectedEmail.attachmentMetadata.length > 0 && (
          <>
            <div className="txn-inbox-detail-divider" />
            <div className="txn-inbox-attachments-section">
              <div className="txn-inbox-attachments-header">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
                <span>Attachments ({selectedEmail.attachmentMetadata.length})</span>
              </div>
              {selectedEmail.attachmentMetadata.map((att, idx) => {
                const fileInfo = getFileIcon(att.filename);
                const hasFile = !!att.gcsPath;
                const key = `${selectedEmail.id}:${idx}`;
                const isDownloading = downloadingKey === key;
                return (
                  <div
                    key={idx}
                    className={`txn-inbox-attachment-card${hasFile ? '' : ' txn-inbox-attachment-card-disabled'}`}
                  >
                    <div className="txn-inbox-attachment-icon" style={{ backgroundColor: fileInfo.color + '15' }}>
                      <span>{fileInfo.icon}</span>
                    </div>
                    <div className="txn-inbox-attachment-info">
                      <span className="txn-inbox-attachment-name">{att.filename}</span>
                      <span className="txn-inbox-attachment-size">
                        {hasFile ? formatFileSize(att.size) : 'Not available for download'}
                      </span>
                    </div>
                    {hasFile && (
                      <button
                        type="button"
                        className="txn-inbox-attachment-download-btn"
                        onClick={() => handleDownloadAttachment(selectedEmail.id, idx, att.filename)}
                        disabled={isDownloading}
                        aria-label={`Download ${att.filename}`}
                        title="Download"
                      >
                        {isDownloading ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" opacity="0.25" />
                            <path d="M22 12a10 10 0 0 1-10 10" />
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }

  // Header — title swaps by active tab, subtitle is the transaction name (mirrors mobile)
  const header = (
    <div className="txn-content-header">
      <div>
        <h2 className="txn-content-title">{activeTab === 'inbox' ? 'Inbox' : 'Sent'}</h2>
        {transactionSubtitle && <p className="txn-inbox-header-subtitle">{transactionSubtitle}</p>}
      </div>
    </div>
  );

  // Tab bar — two pills, count omitted when 0. Active: filled primary. Inactive: light tint.
  const tabBar = (
    <div className="txn-inbox-tabbar">
      <button
        type="button"
        className={`txn-inbox-tab-pill ${activeTab === 'inbox' ? 'txn-inbox-tab-pill-active' : ''}`}
        onClick={() => setActiveTab('inbox')}
      >
        Inbox{sortedInbound.length > 0 ? ` (${sortedInbound.length})` : ''}
      </button>
      <button
        type="button"
        className={`txn-inbox-tab-pill ${activeTab === 'sent' ? 'txn-inbox-tab-pill-active' : ''}`}
        onClick={() => setActiveTab('sent')}
      >
        Sent{sortedOutbound.length > 0 ? ` (${sortedOutbound.length})` : ''}
      </button>
    </div>
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="txn-inbox-section">
        {header}
        {tabBar}
        <div className="txn-inbox-list">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="txn-inbox-card txn-inbox-skeleton-card">
              <div className="txn-inbox-card-row">
                <div className="txn-inbox-avatar txn-inbox-skeleton-avatar" />
                <div className="txn-inbox-card-content">
                  <div className="txn-inbox-skeleton-line" style={{ width: '50%', height: 14 }} />
                  <div className="txn-inbox-skeleton-line" style={{ width: '80%', height: 12 }} />
                  <div className="txn-inbox-skeleton-line" style={{ width: 60, height: 16 }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state — only inbound-fetch failures land here; an outbound-only failure
  // degrades silently to an empty Sent tab (see fetchEmails above).
  if (error) {
    return (
      <div className="txn-inbox-section">
        {header}
        {tabBar}
        <div className="txn-inbox-empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#FF9800" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h3 className="txn-inbox-empty-title">Failed to load inbox</h3>
          <p className="txn-inbox-empty-text">{error}</p>
          <button className="txn-inbox-retry-btn" onClick={fetchEmails}>Try Again</button>
        </div>
      </div>
    );
  }

  const activeList = activeTab === 'inbox' ? sortedInbound : sortedOutbound;

  // Empty state (tab-specific)
  if (activeList.length === 0) {
    return (
      <div className="txn-inbox-section">
        {header}
        {tabBar}
        <div className="txn-inbox-empty-state">
          {activeTab === 'inbox' ? (
            <>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9E9E9E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
                <line x1="2" y1="2" x2="22" y2="22" strokeWidth="2" />
              </svg>
              <h3 className="txn-inbox-empty-title">No received emails</h3>
              <p className="txn-inbox-empty-text">Emails sent to this transaction's address will appear here</p>
            </>
          ) : (
            <>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9E9E9E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
              <h3 className="txn-inbox-empty-title">No sent emails</h3>
              <p className="txn-inbox-empty-text">Offer submissions and other sends from this transaction will appear here</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // Email list
  return (
    <div className="txn-inbox-section">
      {header}
      {tabBar}
      <div className="txn-inbox-list">
        {activeTab === 'inbox' ? (
          sortedInbound.map((email) => {
            const senderName = extractSenderName(email.fromEmail);
            const statusColor = getStatusColor(email.status);

            return (
              <div
                key={email.id}
                className="txn-inbox-card"
                onClick={() => setSelectedEmail(email)}
              >
                <div className="txn-inbox-card-row">
                  <div className="txn-inbox-avatar">
                    {getInitials(senderName)}
                  </div>
                  <div className="txn-inbox-card-content">
                    <div className="txn-inbox-card-top">
                      <span className="txn-inbox-sender">{senderName}</span>
                      <span className="txn-inbox-date">{formatDate(email.receivedAt)}</span>
                    </div>
                    <div className="txn-inbox-subject">{email.subject || '(No subject)'}</div>
                    {email.textBody && (
                      <div className="txn-inbox-preview">{email.textBody.replace(/\s+/g, ' ').trim()}</div>
                    )}
                    <div className="txn-inbox-meta">
                      <DirectionBadge direction="received" />
                      <span
                        className="txn-inbox-status-badge"
                        style={{ backgroundColor: statusColor + '20', color: statusColor }}
                      >
                        {email.status}
                      </span>
                      {email.attachmentCount > 0 && (
                        <span className="txn-inbox-attachment-badge">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                          </svg>
                          {email.attachmentCount}
                        </span>
                      )}
                    </div>
                  </div>
                  <svg className="txn-inbox-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </div>
            );
          })
        ) : (
          sortedOutbound.map((email) => <SentEmailCard key={email.id} email={email} />)
        )}
      </div>
    </div>
  );
};

export default TransactionInbox;
